import React, { useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';
import { useUploadService } from '@bingads-webui-campaign-react/smart-component-image';
import { ItemPicker } from '@bingads-webui-campaign-react/smart-component-card';

import { Dialog, DialogType, DialogFooter } from '@fluentui/react/lib/Dialog';
import { PrimaryButton, DefaultButton } from '@fluentui/react/lib/Button';
import { Dropdown } from '@fluentui/react/lib/Dropdown';

import { SmartReviewCard } from './smart-review-card';

const itemPickerLimit = 10;
const getStyles = () => ({
  reviewsPicker: {
    display: 'block',
  },
  reviewFilterSection: {
    marginBottom: 31,
  },
});

const UnStyledSelectReviewDialog = ({
  i18n,
  classes,
  onAdd,
  onCancel,
  reviews,
  preselectedReviews,
}) => {
  const defaultState = {};
  const [currentReviews, setCurrentReviews] = useState(reviews);

  _.each(preselectedReviews, (review) => {
    defaultState[review] = true;
  });

  const [selectedReviewIds, setSelectedReviewIds] = useState(defaultState);
  const [isLoading, setIsLoading] = useState(false);

  const { bulkUploadImagesByUrlsAsync } = useUploadService();

  const reviewFilterOptions = [
    { key: 'All', text: i18n.getString(_TL_('All')) },
    { key: 'Positive', text: i18n.getString(_TL_('Positive reviews only')) },
    { key: 'Negative', text: i18n.getString(_TL_('Negative reviews only')) },
  ];

  const onFilterChange = (event, item) => {
    const filteredReviews = _.filter(reviews, review => item.key === 'All' || review.recommendationType === item.key);
    setCurrentReviews(filteredReviews);
  };

  const onSelectChange = ({ id, isChecked }) => {
    setSelectedReviewIds(prevState => (
      isChecked ? {
        ...prevState,
        [id]: isChecked,
      } : _.omit(prevState, id)));
  };

  const onAddClicked = () => {
    const selectedReviews = _.filter(currentReviews, review => !!selectedReviewIds[review.id]);
    const photosToUpload = _.compact(_.map(
      selectedReviews,
      review => review.reviewer && !review.reviewer.fbPictureSrc && review.reviewer.picture
    ));

    bulkUploadImagesByUrlsAsync(photosToUpload)
      .finally((() => {
        setIsLoading(true);
        return () => {
          setIsLoading(false);
        };
      })())
      .then((result) => {
        let imageResult = result;
        if (_.isArray(result)) {
          let imageIndex = 0;
          _.map(selectedReviews, (selectedReview) => {
            const newReview = { ...selectedReview };
            if (newReview.reviewer.picture && imageIndex < imageResult.length) {
              const { IdString, ImageUrl } = imageResult[imageIndex];
              newReview.reviewer.fbPictureSrc = newReview.reviewer.picture;
              newReview.reviewer.imageId = IdString;
              // New added picture will use Bing url to replace original picture (fb src)
              newReview.reviewer.picture = ImageUrl;
              imageIndex += 1;
            }
            return newReview;
          });
        } else {
          imageResult = [{ imageId: result.imageId, imageUrl: result.ImageUrl }];
        }
        onAdd({ newSelectedReviews: selectedReviews, imageResult });
      });
  };

  const dialogContentProps = {
    type: DialogType.close,
    title: i18n.getString(_TL_('Select reviews')),
    subText: i18n.getString(_TL_('Add and edit your selection of your Facebook reviews. You can select up to 10.')),
    showCloseButton: false,
    closeButtonAriaLabel: i18n.getString(_TL_('Close')),
  };
  const items = _.filter(currentReviews, review =>
    (_.indexOf(preselectedReviews, review.id) === -1));

  _.map(items, (item) => {
    _.extend(item, {
      selected: !!selectedReviewIds[item.id],
      selectable: _.size(selectedReviewIds) < itemPickerLimit ?
        true : !!selectedReviewIds[item.id],
    });
  });

  const isAddBtnDisabled = () => {
    const checkedItems = [];
    _.map(_.keys(selectedReviewIds), (id) => {
      if (selectedReviewIds[id] && _.indexOf(preselectedReviews, id) === -1) {
        checkedItems.push(id);
      }
    });
    return _.isEmpty(checkedItems);
  };

  return (
    <Dialog
      hidden={false}
      onDismiss={onCancel}
      dialogContentProps={dialogContentProps}
      minWidth={613}
    >
      <UiBlocker blocking={isLoading} loadingText={i18n.getString(_TL_('Adding'))} >
        <div className={classes.reviewFilterSection}>
          <Dropdown
            placeholder={i18n.getString(_TL_('Select an option'))}
            label={i18n.getString(_TL_('Filter reviews'))}
            options={reviewFilterOptions}
            onChange={onFilterChange}
            dropdownWidth={434}
          />
        </div>
        <ItemPicker
          className={classes.reviewsPicker}
          i18n={i18n}
          ItemComponent={SmartReviewCard}
          onSelectChange={onSelectChange}
          selectable
          items={items}
          limit={itemPickerLimit}
          selectedIds={selectedReviewIds}
          noDataLabel={_TL_('There are no Facebook reviews to import from the last 2 years.')}
        />

        <DialogFooter>
          <DefaultButton onClick={onCancel} text={i18n.getString(_TL_('Cancel'))} />
          <PrimaryButton disabled={isAddBtnDisabled()} onClick={onAddClicked} text={i18n.getString(_TL_('Add'))} />
        </DialogFooter>
      </UiBlocker>
    </Dialog>
  );
};

UnStyledSelectReviewDialog.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    reviewsPicker: PropTypes.string.isRequired,
  }).isRequired,
  onAdd: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  reviews: PropTypes.arrayOf(PropTypes.shape({
    reviewText: PropTypes.string.isRequired,
    recommendationType: PropTypes.string.isRequired,
    reviewer: PropTypes.shape({
      name: PropTypes.string.isRequired,
      picture: PropTypes.string,
      imageId: PropTypes.string,
    }).isRequired,
    createdTime: PropTypes.string.isRequired,
    businessName: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
  })).isRequired,
  preselectedReviews: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
  })).isRequired,
};

export const SelectReviewDialog = withDefaultStyles(
  UnStyledSelectReviewDialog,
  getStyles
);
